/* eslint-disable no-shadow */
import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import FadeIn from "react-fade-in";
import { ChartTitle } from "../styles/employer-analytics-styles";
import LeagueTableLine from "./LeagueTableLine";
import {
  Wrapper,
  Table,
  TableHead,
  TableHeadColumn,
  TableHeadColumnText,
  NoData,
} from "../styles/league-table-styles";

export interface ILeagueTableProps {
  agencies: unknown[];
  pdfExport?: boolean;
}

interface Agency {
  id: string;
  recruiter: {
    id: string;
    name: string;
    adminUsers: [{ avatar: string }];
  };
  origin: string;
  placementCount: number;
  engagementCount: number;
  interviewRate: number;
}

export const LeagueTable: FunctionComponent<ILeagueTableProps> = ({
  agencies,
  pdfExport,
}) => {
  const originTooltip =
    "<strong>Agency Origin:</strong><br>A marketplace agency is one you placed with whilst using Portal";
  const intRateTooltip =
    "<strong>Interview Rate:</strong><br>30% or higher is a good interview rate";
  const placementsTooltip =
    "<strong>Placements:</strong><br>The number of placements made for your company";
  const engagementsTooltip =
    "<strong>Engagements:</strong><br>The number of engagements for your company";
  const pdfExportItemsLimit = 20;
  const pdfExportTableLength =
    agencies.length > pdfExportItemsLimit
      ? pdfExportItemsLimit
      : agencies.length;

  return (
    <Wrapper
      className="animated fadeIn delay-3"
      pdfExport={pdfExport}
      pdfExportItemsLimit={pdfExportItemsLimit}
      tableLength={pdfExportTableLength}
    >
      <ChartTitle data-test-id="league-table-charts-title">
        League Table
      </ChartTitle>
      {agencies.length > 0 ? (
        <Table data-test-id="league-table-charts-table" pdfExport={pdfExport}>
          <ReactTooltip
            html
            place="top"
            type="dark"
            effect="solid"
            aria-haspopup="true"
            id="league-table-tooltip"
          />
          <TableHead pdfExport={pdfExport}>
            <TableHeadColumn flex={1} pdfExport={pdfExport}>
              <TableHeadColumnText>Rank</TableHeadColumnText>
            </TableHeadColumn>
            <TableHeadColumn flex={pdfExport ? 3 : 5} pdfExport={pdfExport}>
              <TableHeadColumnText>Agency</TableHeadColumnText>
            </TableHeadColumn>
            <TableHeadColumn
              flex={2}
              pdfExport={pdfExport}
              noSmallDesktop
              data-tip={originTooltip}
              data-for="league-table-tooltip"
            >
              <TableHeadColumnText>
                Origin
                {!pdfExport && <FontAwesomeIcon icon={faInfoCircle} />}
              </TableHeadColumnText>
            </TableHeadColumn>
            <TableHeadColumn
              flex={1}
              pdfExport={pdfExport}
              noSmallDesktop
              data-tip={placementsTooltip}
              data-for="league-table-tooltip"
            >
              <TableHeadColumnText>
                {pdfExport ? "Placements" : "Plac."}
                {!pdfExport && <FontAwesomeIcon icon={faInfoCircle} />}
              </TableHeadColumnText>
            </TableHeadColumn>
            <TableHeadColumn
              flex={1}
              pdfExport={pdfExport}
              noSmallDesktop
              data-tip={engagementsTooltip}
              data-for="league-table-tooltip"
            >
              <TableHeadColumnText>
                {pdfExport ? "Engagements" : "Eng."}
                {!pdfExport && <FontAwesomeIcon icon={faInfoCircle} />}
              </TableHeadColumnText>
            </TableHeadColumn>
            <TableHeadColumn
              flex={1}
              pdfExport={pdfExport}
              noSmallDesktop
              data-tip={intRateTooltip}
              data-for="league-table-tooltip"
            >
              <TableHeadColumnText>
                {pdfExport ? "Interview Rate" : "Int. R."}
                {!pdfExport && <FontAwesomeIcon icon={faInfoCircle} />}
              </TableHeadColumnText>
            </TableHeadColumn>
          </TableHead>
          <FadeIn>
            {pdfExport
              ? agencies
                  .map((agency: Agency, index) => (
                    <LeagueTableLine
                      key={agency.id}
                      rank={index + 1}
                      recruiterId={agency.recruiter.id}
                      name={agency.recruiter.name}
                      avatar={
                        agency.recruiter.adminUsers[0] &&
                        agency.recruiter.adminUsers[0].avatar
                      }
                      origin={agency.origin}
                      placements={agency.placementCount}
                      engagements={agency.engagementCount}
                      interviewRate={agency.interviewRate}
                      originTooltip={originTooltip}
                      intRateTooltip={intRateTooltip}
                      placementsTooltip={placementsTooltip}
                      engagementsTooltip={engagementsTooltip}
                      pdfExport={pdfExport}
                    />
                  ))
                  .slice(0, pdfExportItemsLimit)
              : agencies.map((agency: Agency, index) => (
                  <LeagueTableLine
                    key={agency.id}
                    rank={index + 1}
                    recruiterId={agency.recruiter.id}
                    name={agency.recruiter.name}
                    avatar={
                      agency.recruiter.adminUsers[0] &&
                      agency.recruiter.adminUsers[0].avatar
                    }
                    origin={agency.origin}
                    placements={agency.placementCount}
                    engagements={agency.engagementCount}
                    interviewRate={agency.interviewRate}
                    originTooltip={originTooltip}
                    intRateTooltip={intRateTooltip}
                    placementsTooltip={placementsTooltip}
                    engagementsTooltip={engagementsTooltip}
                    pdfExport={pdfExport}
                  />
                ))}
          </FadeIn>
        </Table>
      ) : (
        <NoData>Data will display when placements have been made</NoData>
      )}
    </Wrapper>
  );
};

LeagueTable.propTypes = {
  agencies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      origin: PropTypes.string,
      placementCount: PropTypes.number,
      engagementCount: PropTypes.number,
      interviewRate: PropTypes.number,
      recruiter: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        adminUsers: PropTypes.arrayOf(
          PropTypes.shape({
            avatar: PropTypes.string,
          })
        ),
      }),
    })
  ).isRequired,
  pdfExport: PropTypes.bool,
};

LeagueTable.defaultProps = {
  pdfExport: null,
};

interface IReduxState {
  employerAnalytics: {
    agencyPerformanceData: {
      agencies: unknown;
    };
  };
}

const mapStateToProps = (state: IReduxState) => ({
  agencies: state.employerAnalytics.agencyPerformanceData.agencies,
});

export default connect(mapStateToProps, {})(LeagueTable);
